import React, { useState, useEffect } from "react";
import {
  Lightbulb,
  FileText,
  FileEdit,
  Cloud,
  Play,
  CheckCircle,
  Database,
  Shield,
  HardDrive,
  Zap,
  Rocket,
  Globe,
  Loader2,
  ExternalLink,
  XCircle,
  LayoutGrid,
} from "lucide-react";
import { useProjectStore } from "@/stores/projectStore";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  UIPayload,
  ActivityEvent,
  Action,
  ThemeContext,
} from "@/lib/agent/payload-parser";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ConnectionSetupHITL } from "./ConnectionSetupHITL";
import { MediaLibraryResultsHITL } from "./media-library-results-hitl";
import { ThemePreviewCard } from "@/components/editor/themes/components/theme-preview-card";
import type { ThemeContent } from "@/types/theme.types";

interface ClaudeResponseCardProps {
  payload: UIPayload;
  markdown: string;
  messageId?: string; // For persisting clicked action state
  onRestore?: () => void;
  onViewCode?: () => void;
  onUpdatePreview?: () => void;
  onPreviewLatest?: () => void;
  onOpenFile?: (filePath: string) => void;
  onAction?: (actionId: string, messageId: string) => void;
  clickedActions?: string[]; // IDs of buttons already clicked (persisted state)
  isStale?: boolean; // True if this message's actions are outdated (user sent new message after)
  className?: string;
  // Theme preview props
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  projectId: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  // Connection setup HITL
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
  // Media library HITL
  devContainerId?: string;
  previewUrl?: string;
}

// Render approve_plan HITL - styled card similar to OptiDev Cloud
function ApprovePlanHITL({
  action,
  isClicked,
  isStale,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  isStale?: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          <span>Plan approved</span>
        </div>
      </div>
    );
  }

  // If stale (user sent a new message after this plan), show "Plan Declined" state
  if (isStale) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>Plan declined</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit">
      <div className="flex items-center gap-2">
        <Play className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Ready to Execute</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Review the plan above and approve to start implementation.
      </p>

      <Button
        variant="default"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-8 px-3 text-xs w-full !rounded-sm"
      >
        <Play className="w-3.5 h-3.5 mr-1.5" />
        {action.label}
      </Button>
    </div>
  );
}

// Render activate_optidev_cloud HITL - styled card with feature highlights
function ActivateOptiDevCloudHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          <span>OptiDev Cloud activated</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit">
      <div className="flex items-center gap-2">
        <Cloud className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Enable OptiDev Cloud</span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Database className="w-3.5 h-3.5" />
          <span>PostgreSQL Database</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          <span>Authentication</span>
        </div>
        <div className="flex items-center gap-1.5">
          <HardDrive className="w-3.5 h-3.5" />
          <span>File Storage</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5" />
          <span>Edge Functions</span>
        </div>
      </div>

      <Button
        variant="default"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-8 px-3 text-xs w-full !rounded-sm"
      >
        <Cloud className="w-3.5 h-3.5 mr-1.5" />
        {action.label}
      </Button>
    </div>
  );
}

// Render publish_project HITL - styled card for publishing confirmation
// Uses projectStore for state synchronization with PublishDropdown
function PublishProjectHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
  currentProjectId,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
  currentProjectId?: string;
}) {
  const publishState = useProjectStore((state) => state.publishState);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Only use store state if it belongs to the current project (prevents cross-project state leakage)
  const isCurrentProjectPublish = publishState.projectId === currentProjectId;

  // Track elapsed time when publishing
  useEffect(() => {
    if (
      isCurrentProjectPublish &&
      publishState.status !== "idle" &&
      publishState.status !== "success" &&
      publishState.status !== "failed"
    ) {
      const interval = setInterval(() => {
        if (publishState.startedAt) {
          setElapsedTime(
            Math.floor((Date.now() - publishState.startedAt) / 1000),
          );
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setElapsedTime(0);
    }
  }, [isCurrentProjectPublish, publishState.status, publishState.startedAt]);

  // Get status text
  const getStatusText = () => {
    switch (publishState.status) {
      case "checking":
        return "Checking for changes...";
      case "queued":
        return "Starting build...";
      case "building":
        return `Building... ${elapsedTime}s`;
      case "deploying":
        return `Deploying... ${elapsedTime}s`;
      case "success":
        return "Published!";
      case "failed":
        return publishState.error || "Publish failed";
      default:
        return "";
    }
  };

  // Success state - show URL (only if this is the current project's publish)
  if (
    isCurrentProjectPublish &&
    publishState.status === "success" &&
    publishState.publicUrl
  ) {
    return (
      <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20 rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Published!</p>
            <a
              href={publishState.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1.5 truncate"
            >
              <span className="truncate">{publishState.publicUrl}</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Failed state (only if this is the current project's publish)
  if (isCurrentProjectPublish && publishState.status === "failed") {
    return (
      <div className="bg-gradient-to-br from-red-500/5 to-red-500/10 border border-red-500/20 rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <XCircle className="w-5 h-5 text-red-500" />
          </div>
          <div className="space-y-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Publish failed
            </p>
            <p className="text-xs text-muted-foreground">
              {publishState.error || "Something went wrong"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In-progress states - only when actually publishing (store-based, not persisted click)
  // After page refresh, isClicked may be true but store is 'idle' - don't show stuck progress
  // Note: 'success' and 'failed' cases are already handled by early returns above
  if (isCurrentProjectPublish && publishState.status !== "idle") {
    return (
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
          </div>
          <div className="space-y-1.5 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {getStatusText()}
            </p>
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 bg-primary/20 rounded-full overflow-hidden min-w-[120px]">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{
                    width:
                      publishState.status === "queued"
                        ? "15%"
                        : publishState.status === "building"
                          ? "50%"
                          : publishState.status === "deploying"
                            ? "85%"
                            : "0%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial state - show publish button
  return (
    <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4 w-fit max-w-[320px]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Share your app</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Get a public URL anyone can access
          </p>
        </div>
      </div>

      <Button
        variant="default"
        size="sm"
        disabled={isClicked}
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-9 px-4 text-sm w-full mt-4 rounded-lg font-medium"
      >
        <Rocket className="w-4 h-4 mr-2" />
        {isClicked ? "Published" : "Publish"}
      </Button>
    </div>
  );
}

// Render theme_approval HITL - uses ThemePreviewCard component
function ThemeApprovalHITL({
  themeContext,
  onAction,
  messageId,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
}: {
  themeContext: ThemeContext;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  workspaceId?: string;
  previewIframe?: HTMLIFrameElement | null;
  projectId?: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
}) {
  // Transform ThemeContext to ThemeContent format for ThemePreviewCard
  const theme = {
    name: themeContext.name,
    type: "registry:theme",
    cssVars: JSON.parse(themeContext.content).cssVars,
    colors: themeContext.colors,
  };

  // Only render if required props are provided
  if (!workspaceId || !projectId) {
    return (
      <div className="w-full max-w-2xl p-4 bg-card border border-border rounded-lg">
        <p className="text-sm text-muted-foreground">
          Unable to display theme preview: missing required project information
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl overflow-hidden">
      <ThemePreviewCard
        theme={theme}
        workspaceId={workspaceId}
        previewIframe={previewIframe || null}
        projectId={projectId}
        onEditTheme={onEditTheme}
        onThemeSaved={onThemeSaved}
      />
    </div>
  );
}

// Render enable_cross_project_sharing HITL
function EnableCrossProjectSharingHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          <span>Cross-project sharing enabled</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit max-w-[320px]">
      <div className="flex items-center gap-2">
        <LayoutGrid className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Cross-Project Sharing</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Enable to read files from other projects in your workspace.
      </p>

      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            setLocalClickedActions((prev) => [...prev, action.id]);
            onAction?.(action.id, messageId || "");
          }}
          className="h-8 px-3 text-xs flex-1 !rounded-sm"
        >
          <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />
          Enable
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Navigate to project settings
            const settingsBtn = document.querySelector(
              "[data-settings-trigger]",
            ) as HTMLButtonElement;
            if (settingsBtn) settingsBtn.click();
          }}
          className="h-8 px-3 text-xs !rounded-sm"
        >
          Settings
        </Button>
      </div>
    </div>
  );
}

// Render cross_project_workspace_disabled HITL — needs admin to change workspace settings
function CrossProjectWorkspaceDisabledHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <Shield className="w-3.5 h-3.5 text-amber-500" />
          <span>Redirected to workspace settings</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit max-w-[320px]">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium">Workspace Sharing Disabled</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Cross-project sharing is disabled at the workspace level. A workspace
        admin needs to enable it in Settings.
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-8 px-3 text-xs w-full !rounded-sm"
      >
        <Shield className="w-3.5 h-3.5 mr-1.5" />
        {action.label}
      </Button>
    </div>
  );
}

export function ClaudeResponseCard({
  payload,
  markdown,
  messageId,
  onRestore,
  onViewCode,
  onUpdatePreview,
  onPreviewLatest,
  onOpenFile,
  onAction,
  clickedActions = [],
  isStale = false,
  className,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
  onConnectionCreated,
  devContainerId,
  previewUrl,
}: ClaudeResponseCardProps) {
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [localClickedActions, setLocalClickedActions] = useState<string[]>([]);

  // Check for HITL type and render specialized components
  if (payload.hitl_type === "approve_plan") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "approve_plan" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <ApprovePlanHITL
            action={action}
            isClicked={isClicked}
            isStale={isStale}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "activate_optidev_cloud") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "activate_optidev_cloud" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <ActivateOptiDevCloudHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "publish_project") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "publish_project" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <PublishProjectHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
            currentProjectId={projectId}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "enable_cross_project_sharing") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "enable_cross_project_sharing" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <EnableCrossProjectSharingHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "cross_project_workspace_disabled") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "go_to_workspace_settings" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <CrossProjectWorkspaceDisabledHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "setup_connection" && payload.connectorType) {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "setup_connection" && Boolean(a.label),
    );
    const isConnectionClicked = action
      ? localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id)
      : false;
    const cancelActionId = "setup_connection_cancelled";
    const isConnectionCancelled =
      localClickedActions.includes(cancelActionId) ||
      clickedActions.includes(cancelActionId);
    return (
      <div className={className}>
        <ConnectionSetupHITL
          connectorType={payload.connectorType}
          isClicked={isConnectionClicked}
          isCancelled={isConnectionCancelled}
          isStale={isStale}
          onConnectionCreated={(
            connectionId,
            connectorType,
            connectionName,
          ) => {
            if (action) {
              setLocalClickedActions((prev) => [...prev, action.id]);
              onAction?.(action.id, messageId || "");
            }
            onConnectionCreated?.(connectionId, connectorType, connectionName);
          }}
          onCancelled={() => {
            setLocalClickedActions((prev) => [...prev, cancelActionId]);
            onAction?.(cancelActionId, messageId || "");
          }}
        />
      </div>
    );
  }

  if (payload.hitl_type === "theme_approval" && payload.themeContext) {
    return (
      <div className={className}>
        <ThemeApprovalHITL
          themeContext={payload.themeContext}
          onAction={onAction}
          messageId={messageId}
          workspaceId={workspaceId}
          previewIframe={previewIframe}
          projectId={projectId}
          onEditTheme={onEditTheme}
          onThemeSaved={onThemeSaved}
        />
      </div>
    );
  }
  console.log(
    "[ClaudeResponseCard] Rendering standard response card with payload:",
    payload,
  );
  if (payload.hitl_type === "media_library_results" && payload.mediaResults) {
    return (
      <div className={className}>
        <MediaLibraryResultsHITL
          mediaType={payload.mediaType || "image"}
          provider={payload.provider || "all"}
          mediaResults={payload.mediaResults}
          summary={payload.summary || ""}
          onAction={onAction}
          messageId={messageId}
        />
      </div>
    );
  }

  // Filter out thoughts from activity list (shown separately at top)
  const nonThoughtActivities = payload.activity.filter(
    (a) => a.type !== "thought",
  );
  const activityToShow = showAllActivity
    ? nonThoughtActivities
    : nonThoughtActivities.slice(0, 5);
  const hasMoreActivity = nonThoughtActivities.length > 5;

  // Calculate total thought time
  const totalThoughtTime = payload.activity
    .filter((a) => a.type === "thought")
    .reduce((sum, a) => sum + (a.duration_seconds || 0), 0);

  const hasChanges = payload.edits_count > 0;

  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg p-4 space-y-4",
        className,
      )}
    >
      {/* Total thought time - in pill */}
      {totalThoughtTime > 0 && (
        <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg w-fit">
          <Lightbulb className="w-3.5 h-3.5" />
          <span>
            Thought for {totalThoughtTime} second
            {totalThoughtTime > 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Markdown content - the actual response text */}
      {markdown && markdown.trim() && (
        <MarkdownRenderer
          content={markdown}
          isStreaming={false}
          className="text-sm"
        />
      )}

      {/* Activity Timeline - tools used in pill, files as plain text */}
      {nonThoughtActivities.length > 0 && (
        <div className="space-y-3">
          {/* Tools used pill */}
          <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg w-fit">
            <Lightbulb className="w-3.5 h-3.5" />
            <span>
              {nonThoughtActivities.length} tool
              {nonThoughtActivities.length > 1 ? "s" : ""} used
            </span>
          </div>

          {/* Activity list - plain text with Hide button */}
          {(showAllActivity || !hasMoreActivity) && (
            <div className="space-y-2">
              {activityToShow.map((activity, index) => (
                <ActivityChip key={index} activity={activity} />
              ))}
            </div>
          )}

          {/* Hide button if more activities */}
          {hasMoreActivity && (showAllActivity || !hasMoreActivity) && (
            <button
              onClick={() => setShowAllActivity(!showAllActivity)}
              className="text-muted-foreground hover:text-foreground text-xs"
            >
              {showAllActivity ? "Hide" : "Show all"}
            </button>
          )}
        </div>
      )}

      {/* Changes section - pill with clickable expand */}
      {hasChanges && (
        <div className="space-y-3">
          {/* Edits made pill with inline Hide button */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowAllChanges(!showAllChanges)}
              className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg hover:bg-accent transition-colors"
            >
              <FileEdit className="w-3.5 h-3.5" />
              <span>
                {payload.edits_count} edit{payload.edits_count > 1 ? "s" : ""}{" "}
                made
              </span>
            </button>
            {payload.changes.length > 0 && showAllChanges && (
              <button
                onClick={() => setShowAllChanges(false)}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                Hide
              </button>
            )}
          </div>

          {/* Changed files list - clickable to open in VS Code */}
          {showAllChanges && payload.changes.length > 0 && (
            <div className="space-y-1.5">
              {payload.changes.map((change, index) => (
                <button
                  key={index}
                  onClick={() => onOpenFile?.(change.file)}
                  className="text-xs text-muted-foreground hover:text-foreground truncate text-left w-full transition-colors"
                  title={`Open ${change.file} in VS Code`}
                >
                  {change.file}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons - only show if there are edits */}
      {/* {hasChanges && (
        <div className="flex items-center justify-end pt-2">
          <div className="flex items-center gap-2">
            {onRestore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRestore}
                className="text-gray-400 hover:text-gray-200 h-7 px-2.5 text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1.5" />
                Restore
              </Button>
            )}
            {onViewCode && (
              <Button
                variant="default"
                size="sm"
                onClick={onViewCode}
                className="bg-blue-600 hover:bg-blue-700 h-7 px-2.5 text-xs"
              >
                <Code className="w-3 h-3 mr-1.5" />
                Code
              </Button>
            )}
          </div>
        </div>
      )} */}

      {/* Design notes */}
      {payload.notes.length > 0 && (
        <div className="space-y-2 text-xs text-muted-foreground italic">
          {payload.notes.map((note, index) => (
            <div key={index}>• {note}</div>
          ))}
        </div>
      )}

      {/* HITL Action buttons - rendered from payload.actions with id and label */}
      {payload.actions &&
        payload.actions.filter((a) => a.id && a.label).length > 0 && (
          <div className="flex justify-end gap-2 pt-2">
            {payload.actions
              .filter(
                (action): action is Action & { id: string; label: string } =>
                  Boolean(action.id && action.label),
              )
              .map((action) => {
                const isClicked =
                  localClickedActions.includes(action.id) ||
                  clickedActions.includes(action.id);
                return (
                  <Button
                    key={action.id}
                    variant={
                      isClicked ? "secondary" : action.variant || "default"
                    }
                    size="sm"
                    onClick={() => {
                      setLocalClickedActions((prev) => [...prev, action.id]);
                      onAction?.(action.id, messageId || "");
                    }}
                    disabled={isClicked}
                    className={cn(
                      "h-8 px-3 text-xs",
                      isClicked &&
                        "bg-muted text-muted-foreground cursor-default",
                    )}
                  >
                    {!isClicked && action.icon === "cloud" && (
                      <Cloud className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {!isClicked && action.icon === "play" && (
                      <Play className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {isClicked ? "Approved" : action.label}
                  </Button>
                );
              })}
          </div>
        )}
    </div>
  );
}

// Activity chip component
function ActivityChip({ activity }: { activity: ActivityEvent }) {
  const getIcon = () => {
    switch (activity.type) {
      case "thought":
        return <Lightbulb className="w-3.5 h-3.5" />;
      case "read":
        return <FileText className="w-3.5 h-3.5" />;
      case "write":
      case "edit":
        return <FileEdit className="w-3.5 h-3.5" />;
      default:
        return <FileText className="w-3.5 h-3.5" />;
    }
  };

  // Don't render thought activities - they're shown at the top
  if (activity.type === "thought") {
    return null;
  }

  const getLabel = (): string => {
    if (activity.path) {
      return activity.path;
    }
    return "";
  };

  const getTypeLabel = (): string => {
    switch (activity.type) {
      case "read":
        return "Read";
      case "write":
        return "Write";
      case "edit":
        return "Edit";
      default:
        return activity.type;
    }
  };

  return (
    <div className="flex items-center gap-2 text-muted-foreground text-xs">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {getIcon()}
        <span>{getTypeLabel()}</span>
      </div>
      <span className="text-xs text-gray-500 truncate">{getLabel()}</span>
    </div>
  );
}
